import { getFace } from '@/catalogs/fonts';
import { getPalette } from '@/catalogs/palettes';
import { getTemplate } from '@/catalogs/templates';
import { applyPalette, createDefaultProject } from '@/core/project/defaults';
import type { OutputFormat, Project, TextAlign } from '@/core/project/types';
import type { ErrorCorrection } from '@/core/qr/encode';
import type { PayloadKind } from '@/core/content/types';

/**
 * Состояние редактора.
 *
 * revision растёт при каждом изменении проекта. Любая асинхронная работа
 * (экспорт, проверка, загрузка шрифта) запоминает revision на момент старта:
 * устаревший результат не имеет права заменить свежий макет, а статус
 * «Проверено» относится ровно к одному состоянию проекта.
 */

export type EditorState = {
  project: Project;
  revision: number;
  past: Project[];
  future: Project[];
  /** Сообщения после импорта проекта — показываются один раз. */
  notes: string[];
};

export type EditorAction =
  | { type: 'set-payload-kind'; kind: PayloadKind }
  | { type: 'set-payload-value'; value: string }
  | { type: 'set-ecc'; ecc: ErrorCorrection }
  | { type: 'set-quiet-zone'; modules: number }
  | { type: 'set-template'; id: string }
  | { type: 'set-palette'; id: string }
  | { type: 'set-qr-color'; role: 'foreground' | 'background'; value: string }
  | { type: 'set-title'; value: string }
  | { type: 'set-caption'; value: string }
  | { type: 'set-note'; value: string }
  | { type: 'set-font'; fontId: string }
  | { type: 'set-weight'; weight: number }
  | { type: 'set-caption-size'; size: number }
  | { type: 'set-align'; align: TextAlign }
  | { type: 'set-width'; widthPx: number }
  | { type: 'set-format'; format: OutputFormat }
  | { type: 'set-decoration'; include: boolean }
  | { type: 'set-jpeg-quality'; quality: number }
  | { type: 'set-transparent'; value: boolean }
  | { type: 'load-project'; project: Project; notes: string[] }
  | { type: 'dismiss-notes' }
  | { type: 'reset' }
  | { type: 'undo' }
  | { type: 'redo' };

const HISTORY_LIMIT = 40;

export function createInitialState(): EditorState {
  return { project: createDefaultProject(), revision: 0, past: [], future: [], notes: [] };
}

function nextProject(project: Project, action: EditorAction): Project {
  switch (action.type) {
    case 'set-payload-kind':
      return { ...project, payload: { ...project.payload, kind: action.kind } };
    case 'set-payload-value':
      return { ...project, payload: { ...project.payload, value: action.value } };
    case 'set-ecc':
      return { ...project, qr: { ...project.qr, errorCorrection: action.ecc } };
    case 'set-quiet-zone':
      return { ...project, qr: { ...project.qr, quietZoneModules: action.modules } };
    case 'set-template': {
      const template = getTemplate(action.id);
      return {
        ...project,
        template: { id: template.id, version: template.version },
        // Выбор шаблона — это оформление, поэтому включаем карточку.
        output: { ...project.output, includeDecoration: true },
      };
    }
    case 'set-palette':
      return applyPalette(project, getPalette(action.id));
    case 'set-qr-color':
      return { ...project, qr: { ...project.qr, [action.role]: action.value } };
    case 'set-title':
      return { ...project, title: action.value };
    case 'set-caption':
      return { ...project, caption: { ...project.caption, text: action.value } };
    case 'set-note':
      return { ...project, note: action.value };
    case 'set-font': {
      const face = getFace(action.fontId, project.caption.weight);
      return {
        ...project,
        caption: {
          ...project.caption,
          fontId: action.fontId,
          weight: face.weight,
          fontRevision: face.revision,
        },
      };
    }
    case 'set-weight': {
      const face = getFace(project.caption.fontId, action.weight);
      return {
        ...project,
        caption: { ...project.caption, weight: face.weight, fontRevision: face.revision },
      };
    }
    case 'set-caption-size':
      return { ...project, caption: { ...project.caption, size: action.size } };
    case 'set-align':
      return { ...project, caption: { ...project.caption, align: action.align } };
    case 'set-width':
      return { ...project, output: { ...project.output, widthPx: action.widthPx } };
    case 'set-format':
      return {
        ...project,
        output: {
          ...project.output,
          format: action.format,
          // JPEG не умеет прозрачность — снимаем флаг сразу, а не при экспорте.
          transparentBackground:
            action.format === 'jpeg' ? false : project.output.transparentBackground,
        },
      };
    case 'set-decoration':
      return { ...project, output: { ...project.output, includeDecoration: action.include } };
    case 'set-jpeg-quality':
      return { ...project, output: { ...project.output, jpegQuality: action.quality } };
    case 'set-transparent':
      return { ...project, output: { ...project.output, transparentBackground: action.value } };
    case 'load-project':
      return action.project;
    case 'reset':
      return createDefaultProject();
    default:
      return project;
  }
}

/**
 * Проект небольшой и состоит только из данных, поэтому сравнение через
 * JSON надёжнее ручного перечисления полей: новое поле не забудут учесть.
 * Нужно, чтобы повторный выбор того же значения не сбрасывал статус
 * проверки и не засорял историю.
 */
function sameProject(a: Project, b: Project): boolean {
  return a === b || JSON.stringify(a) === JSON.stringify(b);
}

export function editorReducer(state: EditorState, action: EditorAction): EditorState {
  if (action.type === 'undo') {
    const previous = state.past[state.past.length - 1];
    if (!previous) return state;
    return {
      ...state,
      project: previous,
      past: state.past.slice(0, -1),
      future: [state.project, ...state.future].slice(0, HISTORY_LIMIT),
      revision: state.revision + 1,
    };
  }
  if (action.type === 'redo') {
    const next = state.future[0];
    if (!next) return state;
    return {
      ...state,
      project: next,
      past: [...state.past, state.project].slice(-HISTORY_LIMIT),
      future: state.future.slice(1),
      revision: state.revision + 1,
    };
  }
  if (action.type === 'dismiss-notes') {
    return state.notes.length === 0 ? state : { ...state, notes: [] };
  }

  const project = nextProject(state.project, action);
  if (sameProject(project, state.project) && action.type !== 'load-project') return state;

  return {
    project,
    revision: state.revision + 1,
    past: [...state.past, state.project].slice(-HISTORY_LIMIT),
    future: [],
    notes: action.type === 'load-project' ? action.notes : [],
  };
}

export const canUndo = (state: EditorState): boolean => state.past.length > 0;
export const canRedo = (state: EditorState): boolean => state.future.length > 0;
